import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthBrand, AuthSide, IconEye, IconWarn, SsoRow } from '../components/auth/AuthBits';
import { googleAuth, register } from '../api';
import Captcha, { CAPTCHA_ENABLED, CAPTCHA_PROMPT } from '../components/Captcha';
import PlanSelection from '../components/PlanSelection';
import { GOOGLE_CLIENT_ID } from '../config';
import '../styles/auth.css';
import Seo from '../components/Seo';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const FREE_MAIL = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com', 'proton.me'];
const STRENGTH = ['', 'Weak', 'Fair', 'Good', 'Strong'];

function scorePassword(p) {
  if (!p) return 0;
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 12) s++;
  if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s++;
  if (/\d/.test(p) && /[^A-Za-z0-9]/.test(p)) s++;
  return Math.min(s, 4);
}

export default function RegisterPage() {
  

  const [v, setV] = useState({ name: '', company: '', email: '', password: '' });
  const [err, setErr] = useState({});
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const captchaRef = useRef(null);
  /* Set once the account exists; switches the page to the plan step, which is
     what the host's Register.js does with its plan-selection modal. */
  const [signedUp, setSignedUp] = useState(null);

  const score = useMemo(() => scorePassword(v.password), [v.password]);
  const freeMail = useMemo(() => {
    const d = v.email.split('@')[1]?.toLowerCase();
    return d ? FREE_MAIL.includes(d) : false;
  }, [v.email]);

  const check = (name, val) => {
    const t = (val || '').trim();
    /* The API splits this into firstName + lastName and enforces 2–50 chars on
       BOTH, so a single word (or an initial) is rejected server-side with a
       confusing "Last name must be between 2 and 50 characters". Catch it here
       while the user is still looking at the field. */
    if (name === 'name') {
      if (!t) return 'Tell us what to call you.';
      const words = t.split(/\s+/).filter(Boolean);
      if (words.length < 2) return 'Enter your first and last name.';
      const first = words[0];
      const last = words.slice(1).join(' ');
      if (first.length < 2) return 'Your first name needs at least 2 letters.';
      if (last.length < 2) return 'Your last name needs at least 2 letters.';
      if (first.length > 50 || last.length > 50) return 'That name is too long (50 characters per part).';
    }
    if (name === 'company') {
      if (!t) return 'Enter your company name.';
      if (t.length < 2) return 'Company name needs at least 2 characters.';
      if (t.length > 100) return 'Company name is too long (100 characters max).';
    }
    if (name === 'email') {
      if (!t) return 'Enter your work email.';
      if (!EMAIL_RE.test(t)) return 'That email address looks incomplete.';
    }
    if (name === 'password') {
      if (!val) return 'Choose a password.';
      if (val.length < 8) return 'Use at least 8 characters.';
    }
    return '';
  };

  const set = (name) => (e) => {
    const val = e.target.value;
    setV((s) => ({ ...s, [name]: val }));
    if (err[name]) setErr((s) => ({ ...s, [name]: check(name, val) }));
  };
  const blur = (name) => (e) => setErr((s) => ({ ...s, [name]: check(name, e.target.value) }));

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    ['name', 'company', 'email', 'password'].forEach((k) => { next[k] = check(k, v[k]); });
    setErr(next);
    const bad = ['name', 'company', 'email', 'password'].find((k) => next[k]);
    if (bad) {
      document.getElementById(bad)?.focus();
      return;
    }
    if (CAPTCHA_ENABLED && !captcha) {
      setErr((s) => ({ ...s, form: CAPTCHA_PROMPT }));
      return;
    }
    setBusy(true);
    setErr((s) => ({ ...s, form: '' }));
    register({
      name: v.name,
      email: v.email,
      password: v.password,
      companyName: v.company,
      recaptchaToken: captcha,
    })
      .then((r) => {
        /* Account exists. Show the plan step in place, same as the host —
           it runs the same public checkout endpoints and the same Razorpay /
           Stripe flow. registrationTokens are kept for the free-plan path,
           which reuses them when the checkout call returns none. */
        const d = r?.data || {};
        setSignedUp({
          userId: d.user?.id,
          registrationTokens: d.token ? { token: d.token, refreshToken: d.refreshToken } : null,
          freePlanActive: !!(d.user?.isActivated && d.user?.subscriptionStatus === 'trialing'),
        });
      })
      .catch((e) => {
        setErr((s) => ({
          ...s,
          form: e?.status === 409 ? 'An account with this email already exists.' : e.message,
        }));
      })
      .catch(() => {})
      .finally(() => {
        setBusy(false);
        try {
          captchaRef.current?.reset();
        } catch { /* widget not mounted */ }
        setCaptcha(null);
      });
  };

  if (signedUp) {
    return (
      <>
        <Seo title="Choose your plan | TeamGrid™" description="Pick a TeamGrid plan to activate your new workspace." noindex />
        <PlanSelection
          userId={signedUp.userId}
          registrationTokens={signedUp.registrationTokens}
          freePlanActive={signedUp.freePlanActive}
        />
      </>
    );
  }

  return (
    <>
      <Seo title="Start Free Trial | TeamGrid™" description="Start a 7-day free TeamGrid trial. Every module unlocked, no credit card, live in five minutes across macOS, Windows and Linux." />
    <div className="ax">
      <div className="ax-form">
        <div className="ax-inner">
          <AuthBrand />

          <>

              <h1 className="ax-h1">Start your free trial</h1>
              <p className="ax-sub">7 days, every module unlocked. No credit card. Already have a workspace? <Link to="/login">Sign in</Link>.</p>

              <SsoRow
                verb="Sign up"
                disabled={busy}
                onError={(m) => setErr((s2) => ({ ...s2, form: m }))}
                onCredential={(idToken) => {
                  if (!idToken) return undefined;
                  /* Signing up with Google still creates a workspace, so the
                     backend needs organizationName — the host asks for it in a
                     follow-up dialog; here the field is already on the form. */
                  const company = v.company.trim();
                  if (company.length < 2) {
                    setErr((s2) => ({ ...s2, company: 'Enter your company name first — Google sign-up still creates a workspace.' }));
                    document.getElementById('company')?.focus();
                    return undefined;
                  }
                  setErr((s2) => ({ ...s2, form: '' }));
                  return googleAuth({ idToken, isSignup: true, organizationName: company })
                    .then((r) => {
                      const d = r?.data || {};
                      setSignedUp({
                        userId: d.user?.id,
                        registrationTokens: d.token ? { token: d.token, refreshToken: d.refreshToken } : null,
                        freePlanActive: !!(d.user?.isActivated && d.user?.subscriptionStatus === 'trialing'),
                      });
                    })
                    .catch((e2) => setErr((s2) => ({
                      ...s2,
                      form: e2?.status === 409
                        ? 'An account already uses that Google address — sign in instead.'
                        : e2?.message || 'Google sign-up failed. Please try again.',
                    })));
                }}
              />
              {GOOGLE_CLIENT_ID && <div className="ax-or">OR USE YOUR WORK EMAIL</div>}

              <form onSubmit={submit} noValidate>
                <div className={`ax-field${err.name ? ' bad' : ''}`}>
                  <div className="ax-lab"><label htmlFor="name">Your name</label></div>
                  <div className="ax-in">
                    <input id="name" name="name" autoComplete="name" placeholder="Priya Nair"
                      value={v.name} onChange={set('name')} onBlur={blur('name')}
                      aria-invalid={!!err.name} aria-describedby={err.name ? 'name-err' : undefined} />
                  </div>
                  {err.name && <p className="ax-err" id="name-err" role="alert"><IconWarn />{err.name}</p>}
                </div>

                <div className={`ax-field${err.company ? ' bad' : ''}`}>
                  <div className="ax-lab"><label htmlFor="company">Company</label></div>
                  <div className="ax-in">
                    <input id="company" name="company" autoComplete="organization" placeholder="Arclight"
                      value={v.company} onChange={set('company')} onBlur={blur('company')}
                      aria-invalid={!!err.company} aria-describedby={err.company ? 'co-err' : undefined} />
                  </div>
                  {err.company && <p className="ax-err" id="co-err" role="alert"><IconWarn />{err.company}</p>}
                </div>

                <div className={`ax-field${err.email ? ' bad' : ''}`}>
                  <div className="ax-lab"><label htmlFor="email">Work email</label></div>
                  <div className="ax-in">
                    <input id="email" name="email" type="email" inputMode="email"
                      autoComplete="email" autoCapitalize="none" spellCheck="false"
                      placeholder="you@company.com" value={v.email}
                      onChange={set('email')} onBlur={blur('email')}
                      aria-invalid={!!err.email} aria-describedby={err.email ? 'em-err' : 'em-hint'} />
                  </div>
                  {err.email ? (
                    <p className="ax-err" id="em-err" role="alert"><IconWarn />{err.email}</p>
                  ) : freeMail ? (
                    <p className="ax-hint" id="em-hint">A work email lets teammates join your workspace automatically.</p>
                  ) : (
                    <p className="ax-hint" id="em-hint">We&apos;ll send the workspace invite here.</p>
                  )}
                </div>

                <div className={`ax-field${err.password ? ' bad' : ''}`}>
                  <div className="ax-lab"><label htmlFor="password">Password</label></div>
                  <div className="ax-in has-btn">
                    <input id="password" name="password" type={show ? 'text' : 'password'}
                      autoComplete="new-password" placeholder="At least 8 characters"
                      value={v.password} onChange={set('password')} onBlur={blur('password')}
                      aria-invalid={!!err.password} aria-describedby={err.password ? 'pw-err' : 'pw-str'} />
                    <button type="button" className="ax-eye" onClick={() => setShow((s) => !s)}
                      aria-label={show ? 'Hide password' : 'Show password'} aria-pressed={show}>
                      <IconEye off={show} />
                    </button>
                  </div>
                  {v.password && !err.password && (
                    <>
                      <div className={`ax-str s${score}`} aria-hidden="true"><i /><i /><i /><i /></div>
                      <p className="ax-strl" id="pw-str" aria-live="polite">Password strength: {STRENGTH[score] || 'Weak'}</p>
                    </>
                  )}
                  {err.password && <p className="ax-err" id="pw-err" role="alert"><IconWarn />{err.password}</p>}
                </div>

                <Captcha ref={captchaRef} onChange={setCaptcha} style={{ marginTop: '18px' }} />

                {err.form && <p className="ax-err" role="alert" style={{ marginTop: '16px' }}><IconWarn />{err.form}</p>}

                <button className="ax-submit" type="submit" disabled={busy}>
                  {busy && <span className="ax-spin" />}
                  {busy ? 'Creating workspace…' : 'Create workspace — free'}
                </button>
                <p className="ax-legal">By creating a workspace you agree to our <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.</p>
              </form>

              <div className="ax-badges"><span>NO CREDIT CARD</span><span>CANCEL ANYTIME</span><span>SOC 2</span></div>
          </>
        </div>
      </div>

      <AuthSide
        eyebrow="7-DAY FREE TRIAL"
        title={<>See how your company <em>actually works</em>.</>}
        points={[
          { b: 'Live in 5 minutes.', t: 'One lightweight agent, 10 computers or 10,000.' },
          { b: 'Every module unlocked.', t: 'Pattern Intelligence, CRM, HRMS and Email Insights included.' },
          { b: 'Nothing auto-charges.', t: 'When the trial ends the workspace simply pauses.' },
        ]}
        quote="We found 6.2 hours of recurring meetings in week one. It paid for itself before the trial ended."
        who="Rahul Menon" role="Founder, Green Soul" initials="RM"
        stats={[{ v: '5 min', l: 'to first insight' }, { v: '0', l: 'screenshots taken' }]}
      />
    </div>
  </>
  );
}
